/**
 * RadarChart - 5-axis pentagon radar chart (SVG)
 *
 * Draws a regular pentagon with grid lines at 0.25/0.5/0.75,
 * fills the data polygon, renders axis labels, and supports
 * overlaying multiple datasets.
 */

import React, { useMemo } from 'react';
import styled, { useTheme } from 'styled-components';

/* ------------------------------------------------------------------ */
/*  Styled helpers                                                     */
/* ------------------------------------------------------------------ */

const Wrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

/* ------------------------------------------------------------------ */
/*  Geometry helpers                                                    */
/* ------------------------------------------------------------------ */

const AXIS_COUNT = 5;
// Start from top (-90 deg) and go clockwise
const angleFor = (i) => (Math.PI * 2 * i) / AXIS_COUNT - Math.PI / 2;

/**
 * Return {x, y} for a point at `radius` distance along axis `i`,
 * centred at `cx, cy`.
 */
const pointAt = (i, radius, cx, cy) => ({
  x: cx + radius * Math.cos(angleFor(i)),
  y: cy + radius * Math.sin(angleFor(i)),
});

/** Build an SVG polygon points string from an array of {x,y}. */
const toPoints = (pts) => pts.map((p) => `${p.x},${p.y}`).join(' ');

/* ------------------------------------------------------------------ */
/*  Default axis labels                                                */
/* ------------------------------------------------------------------ */

const DEFAULT_LABELS = [
  { short: 'AC', full: '行動特性' },
  { short: 'JD', full: '判断基準' },
  { short: 'CH', full: '価値観' },
  { short: 'RS', full: '共感性' },
  { short: 'AW', full: '自己認識' },
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

function RadarChart({
  data = [],
  size = 280,
  color,
  overlays = [],
  showLabels = true,
}) {
  const theme = useTheme();
  const fillColor = color || theme.colors.primary;

  // Derived constants
  const cx = size / 2;
  const cy = size / 2;
  const labelMargin = showLabels ? 38 : 8;
  const maxR = cx - labelMargin;

  /* ---- grid polygons at 25 / 50 / 75 / 100 % ---- */
  const gridPolygons = useMemo(
    () => {
      const levels = [0.25, 0.5, 0.75, 1];
      return levels.map((level) => {
        const pts = Array.from({ length: AXIS_COUNT }, (_, i) =>
          pointAt(i, maxR * level, cx, cy)
        );
        return toPoints(pts);
      });
    },
    [maxR, cx, cy]
  );

  /* ---- axis lines (centre -> vertex) ---- */
  const axisLines = useMemo(
    () =>
      Array.from({ length: AXIS_COUNT }, (_, i) => ({
        ...pointAt(i, maxR, cx, cy),
      })),
    [maxR, cx, cy]
  );

  /* ---- data polygon ---- */
  const dataPolygon = useMemo(() => {
    if (data.length !== AXIS_COUNT) return '';
    const pts = data.map((d, i) =>
      pointAt(i, maxR * Math.min(Math.max(d.value, 0), 1), cx, cy)
    );
    return toPoints(pts);
  }, [data, maxR, cx, cy]);

  /* ---- overlay polygons ---- */
  const overlayPolygons = useMemo(
    () =>
      overlays.map((overlay) => {
        if (!overlay.data || overlay.data.length !== AXIS_COUNT) return null;
        const pts = overlay.data.map((d, i) =>
          pointAt(i, maxR * Math.min(Math.max(d.value, 0), 1), cx, cy)
        );
        return {
          points: toPoints(pts),
          color: overlay.color || theme.colors.secondary,
          opacity: overlay.opacity ?? 0.25,
        };
      }),
    [overlays, maxR, cx, cy, theme.colors.secondary]
  );

  /* ---- label positions (pushed outward from vertices) ---- */
  const labelPositions = useMemo(
    () =>
      Array.from({ length: AXIS_COUNT }, (_, i) => {
        const outer = pointAt(i, maxR + 22, cx, cy);
        return { ...outer, index: i };
      }),
    [maxR, cx, cy]
  );

  return (
    <Wrapper>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* ---- grid polygons ---- */}
        {gridPolygons.map((pts, idx) => (
          <polygon
            key={`grid-${idx}`}
            points={pts}
            fill="none"
            stroke={theme.colors.border}
            strokeWidth={idx === gridPolygons.length - 1 ? 1.5 : 0.7}
            strokeDasharray={idx === gridPolygons.length - 1 ? 'none' : '3,3'}
            opacity={0.6}
          />
        ))}

        {/* ---- axis lines ---- */}
        {axisLines.map((end, i) => (
          <line
            key={`axis-${i}`}
            x1={cx}
            y1={cy}
            x2={end.x}
            y2={end.y}
            stroke={theme.colors.border}
            strokeWidth={0.7}
            opacity={0.5}
          />
        ))}

        {/* ---- overlay fills ---- */}
        {overlayPolygons.map(
          (ov, idx) =>
            ov && (
              <React.Fragment key={`overlay-${idx}`}>
                <polygon
                  points={ov.points}
                  fill={ov.color}
                  fillOpacity={ov.opacity}
                  stroke={ov.color}
                  strokeWidth={1.2}
                  strokeOpacity={0.6}
                />
              </React.Fragment>
            )
        )}

        {/* ---- main data fill ---- */}
        {dataPolygon && (
          <>
            <polygon
              points={dataPolygon}
              fill={fillColor}
              fillOpacity={0.2}
              stroke={fillColor}
              strokeWidth={2}
              strokeLinejoin="round"
            />
            {/* dots at vertices */}
            {data.map((d, i) => {
              const p = pointAt(
                i,
                maxR * Math.min(Math.max(d.value, 0), 1),
                cx,
                cy
              );
              return (
                <circle
                  key={`dot-${i}`}
                  cx={p.x}
                  cy={p.y}
                  r={3.5}
                  fill={fillColor}
                  stroke={theme.colors.bgPrimary}
                  strokeWidth={1.5}
                />
              );
            })}
          </>
        )}

        {/* ---- axis labels ---- */}
        {showLabels &&
          labelPositions.map(({ x, y, index }) => {
            const label = DEFAULT_LABELS[index] || {};
            const dataLabel = data[index]?.label;
            const displayFull = dataLabel || label.full;
            const displayShort = label.short;
            // Adjust text anchor based on angle
            const angle = angleFor(index);
            const cos = Math.cos(angle);
            let anchor = 'middle';
            if (cos > 0.3) anchor = 'start';
            else if (cos < -0.3) anchor = 'end';

            return (
              <g key={`label-${index}`}>
                <text
                  x={x}
                  y={y - 6}
                  textAnchor={anchor}
                  fill={theme.colors.text}
                  fontSize={11}
                  fontWeight={600}
                  fontFamily={theme.fonts.main}
                >
                  {displayFull}
                </text>
                <text
                  x={x}
                  y={y + 8}
                  textAnchor={anchor}
                  fill={theme.colors.textSecondary}
                  fontSize={9}
                  fontFamily={theme.fonts.mono}
                >
                  {displayShort}
                </text>
              </g>
            );
          })}
      </svg>
    </Wrapper>
  );
}

export default RadarChart;
